import { notification } from "antd";
import axios from "axios";
import { MAX_ATTACHMENT_SIZE } from "../utils/attachmentLimits";
import { Buffer } from "buffer";
import httpClient, { httpReports, getErrorMessage, isRetryableError } from "../utils/httpClient";

const allowedFileTypes = ['image/jpeg', 'image/png', 'application/pdf']; // Example allowed types
const maxFileSize = MAX_ATTACHMENT_SIZE;

// Add QCID validation function
const validateParentQcId = (qcId) => {
  if (!qcId) return { valid: true, sanitized: null }; // Optional field
  const sanitized = qcId.replace(/[^0-9]/g, '');
  if (!/^[0-9]{16}$/.test(sanitized)) {
    return { valid: false, error: 'Invalid QCID format. Must be exactly 16 digits.' };
  }
  return { valid: true, sanitized };
};

// Credentials cache
let cachedCredentials = null;

const getSecureCredentials = async () => {
  try {
    // Return cached credentials if available
    if (cachedCredentials) {
      return cachedCredentials;
    }
    
    const response = await axios.get(process.env.REACT_APP_GET_CREDENTIALS);
    cachedCredentials = response.data.credentials;
    return cachedCredentials;
  } catch (error) {
    console.error('Error fetching credentials:', error);
    throw new Error('Failed to fetch credentials');
  }
};

// Function to clear cached credentials
export const clearCredentialsCache = () => {
  cachedCredentials = null;
};

// Function to set credentials manually (for preloading)
export const setCachedCredentials = (credentials) => {
  cachedCredentials = credentials;
};

// Function to check if credentials are cached
export const hasCachedCredentials = () => {
  return cachedCredentials !== null;
};

const createHttpInstance = async () => {
  const credentials = await getSecureCredentials();

  return axios.create({
    baseURL: process.env.REACT_APP_API,
    headers: {
      "Content-type": "application/json",
      "Authorization": `Basic ${credentials}`,
    },
  });
};

export const getJsonFile = async (fileName) => {
  const http = await createHttpInstance();
  const options = {
    fileName: fileName, http
  };
  return http.post("/retrieveJsonFromS3", options);
};

export const uploadJsonToS3 = async (fileName, jsonData) => {
  const http = await createHttpInstance();
  const options = {
    jsonData: jsonData,
    fileName: fileName,
  };
  return http.post("/uploadJsonToS3", options);
};

const sanitizeFileName = (fileName) => {
  // Remove any potentially harmful characters from the file name
  // [OWASP:A05] Also collapse consecutive dots to prevent path traversal sequences (e.g. ../../)
  return fileName.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.{2,}/g, '.');
};

const validateFileType = (fileType, allowedTypes) => {
  // Ensure the file type is allowed
  return allowedTypes.includes(fileType);
};

const validateFileSize = (fileSize, maxSize) => {
  // Ensure the file size is within acceptable limits
  return fileSize <= maxSize;
};

export const sanitizeAndValidateFile = (file, allowedTypes, maxSize) => {
  const sanitizedFileName = sanitizeFileName(file.name);
  const isValidType = validateFileType(file.type, allowedTypes);
  const isValidSize = validateFileSize(file.size, maxSize);

  if (!isValidType) {
    throw new Error('Invalid file type');
  }

  if (!isValidSize) {
    throw new Error('File size exceeds the maximum limit of 10MB');
  }

  return new File([file], sanitizedFileName, { type: file.type });
};

const normalizeBirthdateForDuplicateCheck = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const rawValue = String(value).trim();
  if (!rawValue) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return rawValue;
  }

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const normalized = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return normalized.toISOString().slice(0, 10);
};

export const normalizeChildNameForDuplicateCheck = (information = {}) => {
  const normalizedSurname = String(information.surname || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedFirstName = String(information.firstName || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedMiddleName = String(information.middleName || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedBirthdate = normalizeBirthdateForDuplicateCheck(
    information.birthdate ?? information.childBirthday ?? information.birthday ?? information.dob ?? information.dateOfBirth
  );

  return {
    surname: normalizedSurname,
    firstName: normalizedFirstName,
    middleName: normalizedMiddleName,
    birthdate: normalizedBirthdate,
    fullNameKey: [normalizedSurname, normalizedFirstName, normalizedMiddleName, normalizedBirthdate].filter(Boolean).join(' '),
  };
};

export const checkForDuplicateChildName = async (information = {}) => {
  const normalized = normalizeChildNameForDuplicateCheck(information);

  if (!normalized.surname || !normalized.firstName || !normalized.birthdate) {
    return { duplicate: false, source: 'incomplete-data' };
  }

  const candidateEndpoints = [
    process.env.REACT_APP_CHECK_DUPLICATE_CHILD_NAME,
    process.env.REACT_APP_CHECK_DUPLICATE_CHILD,
    process.env.REACT_APP_API ? `${process.env.REACT_APP_API}/check_duplicate_child_name` : null,
    process.env.REACT_APP_API ? `${process.env.REACT_APP_API}/check-child-name` : null,
  ].filter(Boolean);

  if (!candidateEndpoints.length) {
    return { duplicate: false, source: 'not-configured' };
  }

  for (const endpoint of candidateEndpoints) {
    try {
      // [DUPCHLD1013] Use the same Basic credentials built from env vars as
      // the other case endpoints. createHttpInstance() depends on
      // REACT_APP_GET_CREDENTIALS which is not configured in all environments.
      const credentials = Buffer.from(
        `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
      ).toString('base64');

      const response = await axios.post(
        endpoint,
        {
          surname: normalized.surname,
          firstName: normalized.firstName,
          middleName: normalized.middleName,
          birthdate: normalized.birthdate,
          fullNameKey: normalized.fullNameKey,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${credentials}`,
          },
        }
      );

      const payload = response?.data || {};
      const duplicate = Boolean(payload.duplicate ?? payload.exists ?? payload.isDuplicate ?? payload.hasDuplicate);

      if (payload.duplicate !== undefined || payload.exists !== undefined || payload.isDuplicate !== undefined || payload.hasDuplicate !== undefined) {
        return {
          duplicate,
          source: endpoint,
          message: payload.message || (duplicate ? 'A child with this same name and birthdate already exists in the database.' : 'No duplicate child name found.'),
        };
      }

      if (Array.isArray(payload.records) && payload.records.length > 0) {
        const match = payload.records.some((record) => {
          const candidateBirthdate = normalizeBirthdateForDuplicateCheck(record.birthdate ?? record.birthday ?? record.childBirthday ?? record.dob ?? record.dateOfBirth);
          const candidateSurname = String(record.surname || record.last_name || '').trim().toLowerCase();
          const candidateFirstName = String(record.firstName || record.first_name || '').trim().toLowerCase();
          const candidateMiddleName = String(record.middleName || record.middle_name || '').trim().toLowerCase();
          return candidateSurname === normalized.surname
            && candidateFirstName === normalized.firstName
            && candidateMiddleName === normalized.middleName
            && candidateBirthdate === normalized.birthdate;
        });

        return {
          duplicate: match,
          source: endpoint,
          message: match
            ? 'A child with this same name and birthdate already exists in the database.'
            : 'No duplicate child name found.',
        };
      }
    } catch (error) {
      // Try the next endpoint if one is configured but unavailable.
      console.warn('Duplicate child-name lookup failed:', endpoint, error.message || error);
    }
  }

  return { duplicate: false, source: 'lookup-unavailable' };
};

// Validate all attachments before any case-related save.
const prepareCaseAttachments = (data, medical) => {
  const images = [
    { name: 'certificateOfDisabilityImage', file: data.certificateOfDisabilityImage },
    { name: 'signature', file: medical.signature },
    ...(medical.requirementFiles || []).filter(Boolean).map((file, i) => ({ name: 'requirement_' + i, file })),
  ];
  return images.filter(image => image.file).map(image => {
    try {
      if (image.file.converting) throw new Error('Please wait for image conversion to finish');
      if (!(image.file instanceof Blob)) throw new Error('Please select this attachment again');
      return { ...image, file: sanitizeAndValidateFile(image.file, allowedFileTypes, maxFileSize) };
    } catch (error) {
      throw new Error((image.file.name || image.name) + ': ' + error.message);
    }
  });
};

const formatAddress = (houseNo, street, blkAndLot, barangay, city) => {
  const parts = [houseNo, street, blkAndLot, barangay, city].filter(Boolean);
  return parts.join(' ');
};

/**
 * Creates or updates a client with provided information and family composition.
 * @async
 * @param {object} information - Object containing client information.
 * @param {object[]} familyComposition - Array of objects containing family composition data.
 * @returns {Promise<void>} - Promise that resolves when the client creation process is completed.
 */
export const createClient = async (
  information,
  familyComposition,
  socialService,
  medical
) => {
  try {
    prepareCaseAttachments({
      certificateOfDisabilityImage: information.certificateOfDisability === 'Meron' && information.lastCheckUp != null
        ? information.certificateOfDisabilityImage : null,
    }, medical);
  } catch (error) {
    notification.error({ message: 'Attachment needs attention', description: error.message, duration: 6 });
    return false;
  }

  const handleChangeFileName = (fileData, fileName) => {
    // Create a new File object with the modified name
    const updatedFile = new File([fileData], `${fileName}.${fileData.type.split('/')[1]}`, { type: fileData.type });
    return updatedFile;
  };

  let personalInformation = { ...information };

  if (personalInformation.schoolName === "IbaPa") {
    personalInformation.schoolName = personalInformation.specificSchoolName;
  }

  // Child Education
  const educationalStatusMapping = {
    studying: { lastYearOfStudy: "", reasonForNotStudying: [] },
    stopped: { educationLevel: "", schoolName: "", gradeLevel: "" },
    never_attended_school: { educationLevel: "", schoolName: "", gradeLevel: "", lastYearOfStudy: "" },
  };

  Object.assign(personalInformation, educationalStatusMapping[personalInformation.educationalStatus] || {});

  if (personalInformation.certificateOfDisability === "Meron" && personalInformation.lastCheckUp != null) {
    let date = new Date(personalInformation.lastCheckUp);
    date.setUTCHours(0, 0, 0, 0);
    personalInformation.lastCheckUp = date.toISOString().slice(0, 19).replace('T', ' ');
    personalInformation.certificateOfDisabilityImage = handleChangeFileName(personalInformation.certificateOfDisabilityImage, "certificate");
  } else {
    personalInformation.certificateOfDisabilityImage = null;
    personalInformation.lastCheckUp = null;
  }

  personalInformation.pwdIdNumber = personalInformation.pwdIdStatus !== "Meron" ? 0 : personalInformation.pwdIdNumber;
  personalInformation.childVaccineStatus = personalInformation.vaccineStatus === "Oo" ? personalInformation.vaccineShotCount : "None";
  personalInformation.parentEducationalAttainment = personalInformation.parentEducationalAttainment === "Iba pa" ? personalInformation.parentSpecificEducationalAttainment : personalInformation.parentEducationalAttainment;
  personalInformation.parentPwdRelatedOrg = personalInformation.parentPwdRelatedOrg === "Oo" ? personalInformation.parentPwdRelatedOrgName : personalInformation.parentPwdRelatedOrg;

  const replaceIbaPa = (array, replacement) => array.map(item => item === "IbaPa" ? replacement : item);

  personalInformation.parentSectoralMembership = replaceIbaPa(personalInformation.parentSectoralMembership, personalInformation.parentOtherSectoralMembership);
  personalInformation.parentSocialProtection = replaceIbaPa(personalInformation.parentSocialProtection, personalInformation.parentOtherSocialProtection);

  // Validate and process parentQcId
  if (personalInformation.parentQcIdStatus === "Meron") {
    const qcidValidation = validateParentQcId(personalInformation.parentQcId);
    if (!qcidValidation.valid) {
      throw new Error(qcidValidation.error);
    }
    personalInformation.parentQcId = qcidValidation.sanitized;
  } else {
    personalInformation.parentQcId = 0;
  }
  
  personalInformation.parentVaccineStatus = personalInformation.parentVaccineStatus === "Oo" ? personalInformation.parentVaccineShotCount : "None";

  const mapChallenges = (showTextField, challenges, additionalFields = {}) => ({
    has_challenges: showTextField ? "yes" : "no",
    challenges,
    ...additionalFields
  });

  // Social Service
  socialService.house_ownership = {
    isHouseOwner: socialService.isHouseOwner,
    isHouseOwnerDetails: socialService.isHouseOwnerDetails === "Iba pa" ? socialService.isHouseOwnerDetailsOthers : socialService.isHouseOwnerDetails
  }

  socialService.room_possession = socialService.selectRoomPossession.map(item => ({
    room: item.value,
    room_details: item.selectedSecondaryOption
  }));

  // Medical Background
  let medicalData = {
    therapy: {
      therapy_status: medical.isTherapyDone,
      therapy: medical.isTherapyDone === "yes" ? medical.childTherapyStatus : undefined
    },
    therapy_specialist: {
      has_specialist: medical.hasOtherTherapist,
      recommendation: medical.hasOtherTherapist === "yes" ? medical.therapistRecommendation : undefined
    },
    signature: medical.signature ? handleChangeFileName(medical.signature, "signature") : null,
    playmate_age: medical.childPlaymatesAge,
    separation_issue: medical.childSeparationIssues.includes("At iba pa") ? medical.childSeparationIssues.map(item => item === "At iba pa" ? medical.specificSeparationIssue : item) : medical.childSeparationIssues,
    language_use: medical.childLanguagesUse.includes("At iba pa") ? medical.childLanguagesUse.map(item => item === "At iba pa" ? medical.specificChildLanguagesUse : item) : medical.childLanguagesUse,
    physical_challenges: mapChallenges(medical.showTextFieldA, medical.childPhysicalChallenges, { mode_of_mobility: medical.childModeOfMobility }),
    senses_challenges: mapChallenges(medical.showTextFieldB, medical.childSensesChallenges),
    cognitive_challenges: mapChallenges(medical.showTextFieldC, medical.childCognitiveChallenges, { following_orders: medical.childFollowingInstructions }),
    attitude_challenges: mapChallenges(medical.showTextFieldD, medical.childAttitudeChallenges, { behavior: medical.childBehaviorChallenges }),
    interaction_challenges: mapChallenges(medical.showTextFieldE, medical.childInteractionChallenges),
    requirementFiles: medical.requirementFiles || [],
  };

  try {
    const base64Encode = (str) => Buffer.from(str).toString("base64");

    const credentials = base64Encode(
      `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
    );

    const formData = new FormData();
    formData.append("company", `${information.firstName} ${information.surname}`);
    formData.append("email", information.parentEmailAddress);
    if (information.qcId) {
      formData.append("qcid_no", information.qcId);
    }
    formData.append("personal_details", "");
    formData.append("nationality", "Filipino");
    formData.append("birthdate", information.childBirthday);
    formData.append("gender", information.gender);
    formData.append("phonenumber", information.parentCellphone);
    formData.append(
      "address",
      formatAddress(information.houseNo, information.street, information.blkAndLot, information.barangay, information.city)
    );
    formData.append("barangay", information.barangay);
    formData.append("civil_status", "Single");
    formData.append("remarks", "N/A");
    formData.append("narrative_report", "N/A");
    formData.append("addedfrom", "1");
    formData.append("sector_id", "");
    formData.append("division_id", "5");
    formData.append("first_name", information.firstName);
    formData.append("last_name", information.surname);
    formData.append("middle_name", information.middleName);
    formData.append("extension", information.extension);
    formData.append("birthdate", information.childBirthday);
    formData.append("gender", information.gender);
    formData.append("religion", information.religion);
    formData.append("blood_type", information.bloodType);
    formData.append("house_number", information.houseNo);
    formData.append("blk_and_lot", information.blkAndLot);
    formData.append("street", information.street);
    formData.append("barangay", information.barangay);
    formData.append("district", information.district);
    if (information.zipCode) {
      formData.append("zip_code", information.zipCode);
    }
    formData.append("city", information.city);
    formData.append("height", information.height);
    formData.append("weight", information.weight);
    formData.append("religion", information.religion);
    formData.append("blood_type", information.bloodType);
    formData.append("menstruationInfo", information.hasMenstruationStarted);
    formData.append("circumcisedInfo", information.isCircumcised);

    const response = await axios.post(
      `${process.env.REACT_APP_CREATE_CLIENT}`,
      formData,
      {
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (!response.data.success) {
      // QCID and email may identify different clients; never continue with an ambiguous record.
      if (response.data.conflict) {
        throw new Error('Conflict: QCID and email match different existing clients. Please verify the information.');
      }
      throw new Error(response.data.message);
    }

    const existingClientUpdated = response.data.message === 'updated_successfully';

    const res = await createStaffGuardian(information);
    if (!res) {
      throw new Error('Failed to create staff guardian');
    }

    personalInformation.staff_id = res.data.staff_id;

    const res2 = await createCase(
      response.data.id,
      personalInformation,
      familyComposition,
      socialService,
      medicalData
    );

    if (!res2) {
      return false; // createCase already displayed the specific error.
    }
    if (!res2.data.attachment_errors?.length) notification["success"]({
      message: "Success",
      description: existingClientUpdated
        ? `Existing client information was updated and a new case was created`
        : `Successfully Created New Case`,
      duration: 4,
    });

    return res2;

  } catch (err) {
    console.error(err);
    notification["error"]({
      message: "Error",
      description: err.message || `Something Went Wrong`,
      duration: 4,
    });
  }
};

/**
 * Creates a new staff guardian using provided data.
 * @async
 * @param {object} data - Object containing staff guardian data.
 * @returns {Promise<object>} - Promise that resolves with the created staff guardian data.
 */
const createStaffGuardian = async (data) => {
  try {
    /**
     * Encodes a string to base64.
     * @param {string} str - The string to encode.
     * @returns {string} - The base64 encoded string.
     */
    const base64Encode = (str) => {
      return Buffer.from(str).toString("base64");
    };
    // Encode credentials
    const credentials = base64Encode(
      `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
    );

    const formData = new FormData();
    formData.append("firstname", data.parentFirstName);
    formData.append("lastname", data.parentSurname);
    formData.append("email", data.parentEmailAddress);
    formData.append("prc_lic_no", "0");
    formData.append("position", "guardian");
    formData.append("hourly_rate", "0");
    formData.append("phonenumber", data.parentCellphone);
    formData.append("facebook", "");
    formData.append("linkedin", "");
    formData.append("skype", "");
    formData.append("default language", "");
    formData.append("email_signature", "");
    formData.append("direction", "");
    formData.append("division_id", "5");
    formData.append("sector_id", "");
    formData.append("send_welcome_email", "on");
    formData.append("fakeusernameremembered", "");
    formData.append("fakepasswordremembered", "");
    formData.append("password", "parentQ12345");
    formData.append("role", "5");
    formData.append("permissions[customers][]", "view");
    formData.append("permissions[leads][]", "view");

    const response = await axios.post(
      `${process.env.REACT_APP_CREATE_STAFF_GUARDIAN}`,
      formData,
      {
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.data.status === "success") {
      return response.data;
    }
  } catch (err) {
    console.error(err);
    notification["error"]({
      message: "Error",
      description: `Something Went Wrong`,
      duration: 4,
    });
  }
};

/**
 * Creates a new case using provided data.
 * @async
 * @param {string|number} id - The client ID.
 * @param {object} data - Object containing client and case data.
 * @param {object[]} familyCompo - Array of objects containing family composition data.
 * @returns {Promise<void>} - Promise that resolves when the case is created.
 */
export const createCase = async (id, data, familyCompo, socialService, medical) => {
  try {
    const validImages = prepareCaseAttachments(data, medical);

    /**
     * Encodes a string to base64.
     * @param {string} str - The string to encode.
     * @returns {string} - The base64 encoded string.
     */
    const base64Encode = (str) => {
      return Buffer.from(str).toString("base64");
    };
    // Encode credentials
    const credentials = base64Encode(
      `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
    );

    const currentDate = new Date();

    // Format the date and time for CURRENT_TIME format
    const formattedDate = currentDate.toISOString().slice(0, 19).replace('T', ' ');

    familyCompo = familyCompo.map(item => ({
      ...item,
      vaccine_status: item?.vaccineStatus || '',
      firstname: '',
      birthdate: '',
      relationship: '',
      civil_status: '',
      educational: '',
      occupation: '',
      others: '',
      monthly_peso: '',
      monthly_expenses: "0",
      intake_address: "none",
      educational_status: "none",
      educational_attainment: "none",
      cohabitation_status: "none",
      referring_party: "n/a",
      economic_case: "none",
      date_committed: formattedDate,
      branch_id: 1,
      economic_status_id: 1,
      discharged_date: formattedDate,
      category: "Assessment",
      household_member: "1",
      birth_date: "none",
      birth_place: "none",
      sex: '',
      household_civil_status: "none",
      religion: "none",
      dialect: "tagalog",
      studying: "no",
      employment_type: "none",

      addmission_date: "2024-03-0",
    }));

    const jsonFamilyCompo = JSON.stringify(familyCompo);

    const formData = new FormData();

    formData.append("status", "4");
    formData.append("division_id", "5");
    formData.append("sector_id", "");
    formData.append("source", "1");
    formData.append("assigned", "");
    formData.append("client_id", parseInt(id));
    formData.append("name", data.firstName + " " + data.surname);
    if (data.qcId) {
      formData.append("qcid_no", data.qcId);
    }
    formData.append("phonenumber", data.parentCellphone);
    formData.append("nationality", "Filipino");
    formData.append("email", data.parentEmailAddress);
    formData.append("birthdate", data.childBirthday);
    formData.append("gender", data.gender);
    formData.append("civil_status", "Single");
    formData.append(
      "address",
      formatAddress(data.houseNo, data.street, data.blkAndLot, data.barangay, data.city)
    );
    formData.append("barangay", data.barangay);
    formData.append("description", "N/A");
    formData.append("addedfrom", "10");
    formData.append("service", "Consultation");
    formData.append("interview_date", new Date());
    formData.append("initial_assessment", "Good");
    formData.append("action_taken", "Follow-up");
    formData.append("recommendation", "Continue monitoring");
    formData.append("is_public", "1");
    formData.append("total_family", "1");
    formData.append("family_composition", jsonFamilyCompo);
    formData.append("from_form_id", "0");

    // Child information
    formData.append("staff_id", data.staff_id);
    formData.append("firstName", data.firstName);
    formData.append("lastName", data.surname);
    formData.append("middleName", data.middleName);
    formData.append("extension", data.extension);
    formData.append("birthdate", data.childBirthday);
    formData.append("gender", data.gender);
    formData.append("religion", data.religion);
    formData.append("blood_type", data.bloodType);
    formData.append("house_number", data.houseNo);
    formData.append("blk_and_lot", data.blkAndLot);
    formData.append("street", data.street);
    formData.append("barangay", data.barangay);
    formData.append("district", data.district);
    if (data.zipCode) {
      formData.append("zip_code", data.zipCode);
    }
    formData.append("city", data.city);
    formData.append("height", data.height);
    formData.append("weight", data.weight);
    formData.append("educational_status", data.educationalStatus);
    formData.append("education_level", data.educationLevel);
    formData.append("school_name", data.schoolName);
    formData.append("grade_level", data.gradeLevel);
    formData.append("last_year_of_study", data.lastYearOfStudy);
    formData.append("reason_not_studying", JSON.stringify(data.reasonForNotStudying));
    formData.append("has_menstruation_started", data.hasMenstruationStarted);
    formData.append("is_circumcised", data.isCircumcised);
    formData.append("is_interested_in_arts", data.isInterestedInArts);
    formData.append("is_interested_in_business", data.isInterestedInBusiness);
    formData.append("is_skilled_in_household_chores", data.isSkilledInHouseholdChores);
    formData.append("household_chores_child_can_do", JSON.stringify(data.householdChoresChildCanDo));
    formData.append("certificate_of_disability", data.certificateOfDisabilityImage ? data.certificateOfDisabilityImage.name : null);
    formData.append("last_checkup", data.lastCheckUp);
    formData.append("pwd_id", data.pwdIdNumber);

    // Guardian information
    formData.append("guardian_firstName", data.parentFirstName);
    formData.append("guardian_lastName", data.parentSurname);
    formData.append("guardian_middleName", data.parentMiddleName);
    formData.append("guardian_extension", data.parentExtension);
    formData.append("guardian_birthdate", data.parentBirthday);
    formData.append("guardian_gender", data.parentGender);
    formData.append("guardian_religion", data.parentReligion);
    formData.append("guardian_bloodType", data.parentBloodType);
    formData.append("guardian_height", data.parentHeight);
    formData.append("guardian_weight", data.parentWeight);
    formData.append("guardian_civilStatus", data.parentCivilStatus);
    formData.append("guardian_educationalAttainment", data.parentEducationalAttainment);
    formData.append("guardian_email", data.parentEmailAddress);
    formData.append("guardian_landLine", data.parentLandline);
    formData.append("guardian_phone", data.parentCellphone);
    formData.append("guardian_facebook", data.parentFacebook);
    formData.append("guardian_facebook_url", data.parentFacebookLink || "");
    formData.append("guardian_certificate_of_disability", "");
    formData.append("guardian_pwdOrganization", data.parentPwdRelatedOrgName);
    formData.append(
      "guardian_sectoralMembership",
      JSON.stringify(data.parentSectoralMembership)
    );
    formData.append("guardian_socialProtection", JSON.stringify(data.parentSocialProtection));
    formData.append("guardian_qcId", data.parentQcId);
    formData.append("guardian_isVaccinated", data.parentVaccineShotCount);

    //  medical data
    formData.append("therapy", JSON.stringify(medical.therapy)); // Sample value for 'isTherapyDone'

    // Append the rest of the values with sample values
    formData.append("therapy_specialist", JSON.stringify(medical.therapy_specialist));
    formData.append("signature", medical.signature ? medical.signature.name : null);
    formData.append("playmate_age", medical.playmate_age);
    formData.append("separation_issue", JSON.stringify(medical.separation_issue));
    formData.append("childPlaymatesAge", JSON.stringify(medical.childPlaymatesAge));
    formData.append("language_use", JSON.stringify(medical.language_use));
    formData.append("physical_challenges", JSON.stringify(medical.physical_challenges));
    formData.append("senses_challenges", JSON.stringify(medical.senses_challenges));
    formData.append("cognitive_challenges", JSON.stringify(medical.cognitive_challenges));
    formData.append("attitude_challenges", JSON.stringify(medical.attitude_challenges));
    formData.append("interaction_challenges", JSON.stringify(medical.interaction_challenges));

    // // social service data
    formData.append("house_ownership", JSON.stringify(socialService.house_ownership));
    formData.append("room_possession", JSON.stringify(socialService.room_possession));
    formData.append("daily_food", socialService.dailyFood);
    formData.append("monthly_water", socialService.monthlyWater);
    formData.append("monthly_rent", socialService.monthlyRent);
    formData.append("monthly_load", socialService.monthlyLoad);
    formData.append("monthly_electricity", socialService.monthlyElectricity);
    formData.append("monthly_medicine", socialService.monthlyMedicine);
    formData.append("monthly_grocery", socialService.monthlyGrocery);
    formData.append("monthly_insurance", socialService.monthlyInsurance);
    formData.append("monthly_transportation", socialService.monthlyTransportation);
    formData.append("monthly_allowance", socialService.monthlyAllowance);
    formData.append("monthly_therapy", socialService.monthlyTherapy);
    formData.append("monthly_online_shopping", socialService.monthlyOnlineShopping);
    formData.append("monthly_hygiene", socialService.monthlyHygiene);
    formData.append("family_possession", JSON.stringify(socialService.appliancesOwned));
    formData.append("house_visit_permission", socialService.agreement);

    // First, create the new case and wait for its response
    const response = await axios.post(
      `${process.env.REACT_APP_CREATE_NEW_CASE}`,
      formData,
      {
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Check if the case creation was successful
    if (response.data.success === true) {

      const caseId = response.data.id; // Get the case ID from the response

      const attachmentErrors = [];

      // Loop over the array and make the axios post request for each image
      for (const image of validImages) {
        try {
          const attachmentData = new FormData();
          attachmentData.append('id', caseId);
          attachmentData.append('last_file', true);
          attachmentData.append('file', image.file);
          const uploadResponse = await axios.post(
            `${process.env.REACT_APP_CREATE_ATTACHMENT}`,
            attachmentData,
            {
              headers: {
                Authorization: `Basic ${credentials}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (uploadResponse.data?.success === false || uploadResponse.data?.status === 'error') {
            throw new Error('Attachment upload failed');
          }
        } catch (error) {
          attachmentErrors.push(image.file.name || image.name);
          notification.error({
            message: 'File Upload Error',
            description: 'Case saved (' + (response.data.reference_number || caseId) + '); failed to upload ' + (image.file.name || image.name) + '.',
            duration: 0,
          });
        }
      }
      if (attachmentErrors.length) response.data.attachment_errors = attachmentErrors;

      // Return response after successfully uploading files
      return response;

    } else {
      // If case creation fails, show error notification
      notification["error"]({
        message: "Error",
        description: response.data.message,
        duration: 4,
      });
      return false;
    }
  } catch (err) {
    console.error(err);
    notification["error"]({
      message: "Error",
      description: err.message || `Unable to create case`,
      duration: 4,
    });
    return false;
  }
};

export const getApplicationStatus = async (referenceNumber) => {
  try {
    /**
     * Encodes a string to base64.
     * @param {string} str - The string to encode.
     * @returns {string} - The base64 encoded string.
     */
    const formData = new FormData();

    const base64Encode = (str) => {
      return Buffer.from(str).toString("base64");
    };
    // Encode credentials
    const credentials = base64Encode(
      `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
    );
    formData.append('reference_number', referenceNumber);

    const response = await axios.post(
      `${process.env.REACT_APP_GET_APPLICATION_STATUS}`,
      formData,
      {  // Moved the headers outside the data object
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const data = response.data;
    if (data.message === "no found") {
      notification["error"]({
        message: "Error",
        description: `Unable to find reference number`,
        duration: 4,
      });
    } else {
      notification["success"]({
        message: "Success",
        description: `Data loaded successfully`,
        duration: 4,
      });
    }
    return response;
  } catch (err) {
    console.error(err);
    notification["error"]({
      message: "Error",
      description: `Something Went Wrong`,
      duration: 4,
    });
  }
};

export const getStatusList = async (referenceNumber) => {
  try {
    const formData = new FormData();

    const base64Encode = (str) => {
      return Buffer.from(str).toString("base64");
    };
    // Encode credentials
    const credentials = base64Encode(
      `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
    );
    formData.append('reference_number', referenceNumber);

    const response = await axios.post(
      `${process.env.REACT_APP_GET_STATUS_LIST}`,
      formData,
      {  // Moved the headers outside the data object
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (err) {
    console.error(err);
    notification["error"]({
      message: "Error",
      description: `Something Went Wrong`,
      duration: 4,
    });
  }
};

export const sendEmail = async (data) => {
  try {
    const formData = new FormData();

    const base64Encode = (str) => {
      return Buffer.from(str).toString("base64");
    };
    // Encode credentials
    const credentials = base64Encode(
      `${process.env.REACT_APP_USERNAME}:${process.env.REACT_APP_PASSWORD_ADMIN}`
    );
    formData.append('email', data.email);
    formData.append('message', data.message);
    formData.append('subject', data.subject);

    const response = await axios.post(
      `${process.env.REACT_APP_SEND_EMAIL}`,
      formData,
      {
        headers: {
          Authorization: `Basic ${credentials}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    // Success notification
    notification["success"]({
      message: "Success",
      description: "Email sent successfully",
      duration: 4,
    });

    return response;
  } catch (err) {
    console.error(err);
    notification["error"]({
      message: "Error",
      description: `Something Went Wrong`,
      duration: 4,
    });
  }
};